import { DotMatrix } from '@clickguard/design-system'
import { useAuth0 } from '@auth0/auth0-react'
import { AssetDTO } from 'api-models'
import { GButton, GComboBox } from 'components/basic-blocks'
import { OnboardingLayout } from 'components/onboarding-layout'
import {
	clearShopifyClaimIntent,
	normalizeShop,
	readShopifyClaimIntent
} from 'components/shopify-install/use-shopify-connect'
import { useUrlQuery } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AdAccountLinks } from 'services/user/ad-account-links'
import { Assets } from 'services/user/assets'
import { AllAccountsAssets } from 'services/user/types'
import { parseError } from 'utils/errors'

const assetSettingsUrl = (accountId: string, assetId: string) =>
	`/settings/workspace/${accountId}/asset/${assetId}/tracking-code`

// Mirrors the backend ShopifyError.STORE_ALREADY_BOUND message — surfaced on the origin wizard tab
// when a ClickGUARD-initiated install lands on a store that's already active on another asset.
const STORE_ALREADY_BOUND_MESSAGE =
	'This store is already connected to another ClickGUARD account/asset. Disconnect it there first.'
const DONE_CONNECTED = 'Your Shopify store is connected. You can close this tab and return to ClickGUARD.'
const DONE_FAILED = 'We couldn’t finish connecting. Head back to the ClickGUARD tab to see what happened.'

// Shared card treatment so every claim-page state sits on the same branded OnboardingLayout
// background as the wrong-workspace screen (matches the join-workspace look).
const CARD_SHADOW = { boxShadow: '0px 12px 16px 0px rgba(16,24,40,0.08), 0px 4px 6px 0px rgba(16,24,40,0.03)' }
const CARD_CLASS = 'bg-white rounded-3xl p-8 max-w-md w-full'

/**
 * Shopify claim page (DEV-2365 §2.3) — the landing spot for a Shopify-*initiated* install.
 *
 * A fresh install starts from the Shopify App Store (OAuth can't begin without a shop), so the app
 * OAuths in before it's bound to any ClickGUARD asset. The backend holds the token as a pending
 * install and redirects the (now authenticated — App enforces login on this non-public route)
 * merchant here.
 *
 * Two paths, distinguished by the stashed claim intent (NOT window.opener — Shopify's cross-origin
 * redirects sever it):
 *   • Auto-bind — the merchant started from the ClickGUARD connect wizard, which stashed the target
 *     asset. We claim to it straight away; the wizard tab is already polling, so we just close this
 *     one (relaying any error — e.g. the store is already bound elsewhere — via localStorage).
 *   • Manual — a true App-Store-first install (no intent). We resolve the shop's status:
 *       - already bound to a workspace the user CAN access → land them on that asset's settings;
 *       - already bound to a workspace they CANNOT access → the invite-style "different workspace"
 *         page (log out / switch account), mirroring the join-workspace wrong-account screen;
 *       - not bound (pending) → the workspace/asset picker;
 *       - nothing pending → "install expired, reopen from Shopify".
 *
 * Either way this satisfies Shopify App Store requirement 2.3.3 (land in the app UI after install).
 */
export function ShopifyClaimPage() {
	const query = useUrlQuery()
	const navigate = useNavigate()
	const { logout } = useAuth0()
	const shop = normalizeShop(query.shop || '')

	const [loading, setLoading] = useState(true)
	// Auto-bind finished → this tab should close; rendered as a fallback if window.close() is blocked.
	const [doneMessage, setDoneMessage] = useState<string | null>(null)
	// Shop is bound to a workspace the current user can't access → show the switch-account page.
	const [wrongWorkspace, setWrongWorkspace] = useState(false)
	// null = still loading; false = pending install expired/absent; true = ready to claim.
	const [pending, setPending] = useState<boolean | null>(null)
	const [accounts, setAccounts] = useState<AllAccountsAssets[]>([])
	const [accountId, setAccountId] = useState<string>('')
	const [assetId, setAssetId] = useState<string>('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		let cancelled = false
		;(async () => {
			if (!shop) {
				if (!cancelled) {
					setError('Missing Shopify store. Reopen the app from your Shopify admin.')
					setLoading(false)
				}
				return
			}

			// Auto-bind path: the connect wizard stashed the asset to bind this store to. The wizard tab
			// is already open and awaiting the connection, so we finish silently and close this tab —
			// relaying success/failure via localStorage (shared across tabs), which its poll reads.
			const intent = readShopifyClaimIntent()
			if (intent) {
				try {
					// Resolve the store's binding first. This also covers the "opened an already-active store"
					// entry (via GET /shopify/app), where no pending token was stashed — so we must NOT just
					// call claim (that would report "expired"); we detect the existing binding here instead.
					const status = await AdAccountLinks.getShopifyStatus(intent.accountId, shop)
					if (cancelled) return

					if (status.bound) {
						const sameBinding =
							status.boundAccountId === intent.accountId && status.boundAssetId === intent.assetId
						clearShopifyClaimIntent()
						if (sameBinding) {
							// Already connected to the intended asset — the origin tab's poll already sees it.
							setDoneMessage(DONE_CONNECTED)
						} else {
							// Active on a different account/asset → surface the error on the origin wizard tab.
							window.localStorage.setItem('shopify_oauth_error', STORE_ALREADY_BOUND_MESSAGE)
							setDoneMessage(DONE_FAILED)
						}
						window.close()
						return
					}

					// Not bound (brand-new store, or one disconnected from another asset) → bind it to the
					// intended asset. Requires the pending token stashed by the OAuth callback.
					await AdAccountLinks.claimShopify(intent.accountId, { shop, asset_id: intent.assetId })
					clearShopifyClaimIntent()
					if (cancelled) return
					setDoneMessage(DONE_CONNECTED)
					window.close()
				} catch (err) {
					clearShopifyClaimIntent()
					if (cancelled) return
					const message = parseError(err) || 'Could not connect the store. Please try again.'
					window.localStorage.setItem('shopify_oauth_error', message)
					setDoneMessage(DONE_FAILED)
					window.close()
				}
				return
			}

			// Manual path: load the user's workspaces (with assets), then resolve the shop's status.
			try {
				const all = (await Assets.findAllAssets()).filter((a) => (a.assets?.length ?? 0) > 0)
				if (cancelled) return
				setAccounts(all)
				if (all.length === 0) {
					setLoading(false)
					return
				}

				const status = await AdAccountLinks.getShopifyStatus(all[0].id, shop)
				if (cancelled) return

				if (status.bound) {
					const owned = all.find((a) => a.id === status.boundAccountId)
					if (owned && status.boundAssetId) {
						// The user owns this binding. Apply the fresh token if one is pending (e.g. reinstall
						// after the token died), then deep-link into that asset's settings.
						if (status.pending) {
							await AdAccountLinks.claimShopify(owned.id, { shop, asset_id: status.boundAssetId })
						}
						if (cancelled) return
						navigate(assetSettingsUrl(owned.id, status.boundAssetId))
						return
					}
					// Bound to a workspace this account can't access → offer to switch accounts.
					setWrongWorkspace(true)
					setLoading(false)
					return
				}

				if (!status.pending) {
					setPending(false) // nothing to claim — the install lapsed
					setLoading(false)
					return
				}

				setAccountId(all[0].id)
				setAssetId(all[0].assets?.[0]?.id ?? '')
				setPending(true)
			} catch (err) {
				if (!cancelled) setError(parseError(err) || 'Something went wrong loading your workspaces.')
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [shop])

	const selectedAccount = useMemo(() => accounts.find((a) => a.id === accountId), [accounts, accountId])
	const assetOptions: AssetDTO[] = selectedAccount?.assets ?? []
	const selectedAsset = useMemo(() => assetOptions.find((a) => a.id === assetId), [assetOptions, assetId])

	const onAccountChange = (account: AllAccountsAssets) => {
		setAccountId(account.id)
		setAssetId(account.assets?.[0]?.id ?? '')
	}

	// Manual (Shopify-first) claim: no wizard tab is waiting, so deep-link into the asset's settings.
	const connect = async () => {
		if (!accountId || !assetId) {
			setError('Choose a workspace and asset to connect the store to.')
			return
		}
		setSubmitting(true)
		setError(null)
		try {
			await AdAccountLinks.claimShopify(accountId, { shop, asset_id: assetId })
			toast.success('Shopify store connected')
			navigate(assetSettingsUrl(accountId, assetId))
		} catch (err) {
			const message = parseError(err) || 'Could not connect the store. Please try again.'
			// A lapsed token can only be recovered by reopening from Shopify (the code is single-use).
			if (message.toLowerCase().includes('expired')) setPending(false)
			setError(message)
			toast.error(message)
		} finally {
			setSubmitting(false)
		}
	}

	// Auto-bind finished — this tab should close (no user input needed). Plain white fallback message
	// in case window.close() was blocked; no branded background for a tab that's on its way out.
	if (doneMessage) {
		return (
			<div className="w-full h-screen flex items-center justify-center bg-white px-6">
				<p className="max-w-sm text-center text-base text-t-secondary">{doneMessage}</p>
			</div>
		)
	}

	// Store is connected to another workspace this account can't reach — mirror the join-workspace
	// wrong-account screen: explain, and let them log out to sign back in with an account that has access.
	if (wrongWorkspace) {
		return (
			<OnboardingLayout showStepper={false} headerVariant="sign-up">
				<div className={`${CARD_CLASS} text-center`} style={CARD_SHADOW}>
					<div className="text-xl font-semibold text-t-default mb-4">
						This store is connected to a different workspace
					</div>
					<p className="text-[color:var(--text-secondary)] mb-6">
						<span className="font-semibold">{shop}</span> is already connected to another ClickGUARD workspace that
						this account can’t access. Log out and sign back in with an account that has access, or disconnect it from
						that workspace first.
					</p>
					<button
						onClick={() => logout({ returnTo: window.location.href })}
						className="w-full px-6 py-3 bg-[#EEFF9D] border border-[#BAE100] text-on-lemon rounded-full hover:bg-[#D2FB0C] transition-colors font-medium shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)]"
					>
						Log out and continue
					</button>
				</div>
			</OnboardingLayout>
		)
	}

	// Resolving the store (and, on the auto-bind path, connecting it) — no user input needed yet, so a
	// plain white loader with progress text, not the branded background. If it turns out the merchant
	// must pick a workspace, we swap to the branded picker below once loading clears.
	if (loading) {
		return (
			<div className="w-full h-screen flex flex-col items-center justify-center gap-4 bg-white px-6 text-center">
				<DotMatrix size="lg" tone="inherit" className="text-primary-500" />
				<span className="text-t-secondary">Connecting your Shopify store…</span>
				<span className="max-w-sm text-sm text-t-checkbox-unactive">
					Installing the app and setting up conversion tracking. This only takes a moment.
				</span>
			</div>
		)
	}

	return (
		<OnboardingLayout showStepper={false} headerVariant="sign-up">
			<div className={`${CARD_CLASS} space-y-6`} style={CARD_SHADOW}>
				<div className="space-y-1">
					<h1 className="text-xl font-semibold text-t-default">Connect your Shopify store</h1>
					<p className="text-sm text-t-secondary">
						{shop ? (
							<>
								Choose the workspace and asset to connect <span className="font-medium">{shop}</span> to.
							</>
						) : (
							'Reopen the app from your Shopify admin to reconnect.'
						)}
					</p>
				</div>

				{error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">{error}</div>}

				{pending === false && (
					<div className="text-sm text-amber-700 bg-amber-100 rounded-lg px-4 py-3">
						This Shopify install has expired. Reopen the app from your Shopify admin to reconnect your store.
					</div>
				)}

				{pending !== false && accounts.length === 0 && (
					<div className="space-y-4">
						<div className="text-sm text-t-secondary">
							You don’t have a workspace with an asset yet. Create one in ClickGUARD first, then reopen the app
							from your Shopify admin.
						</div>
						<GButton label="Go to ClickGUARD" color="primary" onClick={() => navigate('/')} />
					</div>
				)}

				{pending === true && accounts.length > 0 && (
					<div className="space-y-4">
						<GComboBox<AllAccountsAssets>
							label="Workspace"
							options={accounts}
							value={selectedAccount}
							onChange={onAccountChange}
							filter={(a, q) => a.name.toLowerCase().includes(q.toLowerCase())}
							renderValue={(a) => a.name}
							renderOption={(a) => a.name}
						/>
						<GComboBox<AssetDTO>
							label="Asset"
							options={assetOptions}
							value={selectedAsset}
							onChange={(a) => setAssetId(a.id)}
							filter={(a, q) => a.name.toLowerCase().includes(q.toLowerCase())}
							renderValue={(a) => a.name}
							renderOption={(a) => a.name}
						/>
						<GButton
							label="Connect store"
							color="primary"
							className="w-full"
							loading={submitting}
							disabled={submitting || !accountId || !assetId}
							onClick={connect}
						/>
					</div>
				)}
			</div>
		</OnboardingLayout>
	)
}
